import {
  DateFilterType,
  EmployeesFilterType,
  JobSchedulesFilterType,
  JobsFilterType,
  MachineFilterType,
  VehicleFilterType
} from '@/enum'
import {
  CustomFilterTypeExpression,
  DateTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListOrderingTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression,
  NewAlgoTypeExpression
} from '@/enum/LinqExpressionType'

import type { Filters, FilterValue } from '@/types/service-types/FilterExpressionGenerator.d'

class FilterExpressionGenerator {
  private filters: Filters

  constructor(filters: Filters) {
    this.filters = filters
  }

  public generateFilterExpression(): string {
    const expressions: string[] = []
    Object.entries(this.filters).forEach(([key, filter]) => {
      if (key !== 'Preference') {
        this.processFilterKey(key, filter, expressions)
      }
    })

    return expressions.join('&')
  }

  public generatePreferenceExpression = (
    property: string,
    preference: string,
    values: boolean | string | number | (boolean | string | number)[]
  ) => {
    const [associatedResource, resourceProperty] = property.split('.')
    const operator = ''
    // if (preference === PreferenceFilterType.Available) {
    //   operator = '!';
    // }
    return `${property}.Any(x=>x.${preference}="${values}")`
  }

  public generatePreferenceExpressionForObjectProperty = (
    property: string,
    preference: string,
    values: boolean | string | number | (boolean | string | number)[]
  ) => {
    if (Array.isArray(values)) {
      const expression = `${property}.Any(${values.map((value) => `${preference}="${value}"`).join('||')})`
      return `(${expression})`
    }
    return `${property}.Any(${preference}="${values}")`
  }

  private processFilterKey(key: string, filter: FilterValue, expressions: string[]): void {
    if (Array.isArray(filter)) {
      expressions.push(`${key}=${filter.join(',')}`)
    } else if (this.isSpecificKey(key)) {
      expressions.push(`${key}=${encodeURIComponent(filter as any)}`)
    } else if (this.isNestedObjectFilter(key, filter)) {
      const nestedExpression = this.generateNestedExpression(filter as Record<string, any>)
      expressions.push(`${key}=${encodeURIComponent(nestedExpression)}`)
    } else if (key === ListOrderingTypeExpression.Ordering && typeof filter === 'object') {
      const orderingExpression = this.generateOrderingExpression(filter)
      expressions.push(`${key}=${encodeURIComponent(orderingExpression)}`)
    } else if (key === ItemTypeExpression.Filter && typeof filter === 'string') {
      expressions.push(`${key}=${encodeURIComponent(filter)}`)
    } else if (typeof filter === 'number') {
      expressions.push(`${key}=${filter}`)
    }
  }

  private generateNestedExpression(filter: Record<string, any>): string {
    let nestedExpression = ''

    Object.entries(filter).forEach(([subKey, subFilter]) => {
      if (
        [
          JobsFilterType.SCHEDULED_DATE as string,
          JobsFilterType.NEXT_TRIGGER_INFO_DUE_AT,
          JobsFilterType.COMPLETION_DATE,
          JobsFilterType.CREATED_AT,
          MachineFilterType.COMMISSION_DATE,
          MachineFilterType.DECOMMISSION_DATE,
          VehicleFilterType.MOT_DATE,
          DateFilterType.TIMESTAMP,
          JobSchedulesFilterType.NEXT_TRIGGER_DATE
        ].includes(subKey)
      )
        return
      if (nestedExpression) nestedExpression += '&&'

      if (subKey === CustomFilterTypeExpression.Expression) {
        nestedExpression += `${subFilter}`
      } else if (typeof subFilter === 'object' && !Array.isArray(subFilter)) {
        nestedExpression += this.generateComplexExpression(subKey, subFilter)
      } else if (subKey?.includes(EmployeesFilterType.ROLE)) {
        nestedExpression += this.generatePreferenceExpressionForObjectProperty(
          EmployeesFilterType.ROLE,
          'Name',
          subFilter
        )
      } else if (subKey.includes(JobSchedulesFilterType.MAINTENANCE_TYPE)) {
        nestedExpression += this.generatePreferenceExpressionForObjectProperty(
          JobSchedulesFilterType.MAINTENANCE_TYPE,
          'TypeId',
          subFilter
        )
      } else if (subKey.includes(JobsFilterType.ASSIGNED_EMPLOYEES)) {
        nestedExpression += this.generatePreferenceExpressionForObjectProperty(
          JobsFilterType.ASSIGNED_EMPLOYEES,
          'EmployeeId',
          subFilter
        )
      } else {
        nestedExpression += this.generateExpression(subKey, '=', subFilter)
      }
    })

    return nestedExpression
  }

  private generateComplexExpression(subKey: string, subFilter: Record<string, any>): string {
    return Object.entries(subFilter)
      .map(([operator, value]) => this.generateExpression(subKey, operator, value))
      .join('')
  }

  private generateOrderingExpression(filter: Record<string, any>): string {
    return Object.entries(filter)
      .map(([orderKey, orderValue]) => `${orderKey} ${orderValue}`)
      .join(',')
  }

  private generateExpression(
    property: string,
    operator: string,
    values: boolean | string | number | (string | number)[]
  ): string {
    const allowedOperators = ['==', '=', '!=', '<', '>', '<=', '>=']

    if (!allowedOperators.includes(operator)) {
      throw new Error(`Unsupported operator: ${operator}`)
    }

    if (typeof values === 'boolean' || typeof values === 'number') {
      return `${property}${operator}${values}`
    }

    if (Array.isArray(values)) {
      return values
        .map((value, index) => {
          const formattedValue =
            typeof value === 'boolean' || typeof value === 'number' ? value : `"${value}"`
          return `${index === 0 ? '(' : '||'}${property}${operator}${formattedValue}${index === values.length - 1 ? ')' : ''}`
        })
        .join('')
    }

    return `${property}${operator}"${values}"`
  }

  private isSpecificKey(key: string): boolean {
    return [
      DateTypeExpression.start,
      DateTypeExpression.end,
      ListFilterTypeExpression.SearchText,
      DeleteTypeExpression.SearchText,
      ListResponseTypeExpression.Expand,
      ListResponseTypeExpression.Select,
      ItemTypeExpression.Expand,
      NewAlgoTypeExpression.UseNewAlgo,
      ListPagingTypeExpression.Cookie,
      NewAlgoTypeExpression.DisableFiltering.toLowerCase()
    ].includes(key)
  }

  private isNestedObjectFilter(key: string, filter: FilterValue): boolean {
    return (
      [
        ListFilterTypeExpression.Filter as string,
        DeleteTypeExpression.Expression as string,
        ItemTypeExpression.Filter as string
      ].includes(key) && typeof filter === 'object'
    )
  }
}

export default FilterExpressionGenerator
